import { GetUsedCarsResponse, SearchFilters } from '@cinch-nx/data-search'
import { useSsrExperiments } from '@cinch-nx/adobe-server-side'
import { Env, readFromEnv } from '@cinch-nx/environments'
import axios from 'axios'
import { useRouter } from 'next/router'
import useSWR from 'swr'
import {
  getNaturalLanguageQueryFromUrlPath,
  getUsedCarsApiRequestUrlForUrlPath,
} from '../../utils'
import { searchFilterKeys } from '../../constants/filter-constants'
import { useSearchStore } from '../use-search-store/use-search-store'
import { useShallow } from 'zustand/react/shallow'
import { useEffect, useMemo, useState } from 'react'
import isEqual from 'lodash/isEqual'
import { keepPreviousDataWhileLoadingNewDataMiddleware } from '../../swr-middleware/keep-previous-data-while-loading-new-data-middleware'
import { scrollToTarget } from '../../utils/scroll-to-target/scroll-to-target'

const httpClient = axios.create()

const fetcher = async (
  url: string,
  shouldUseDataDrivenSortOrderAsDefault: boolean,
): Promise<GetUsedCarsResponse> =>
  httpClient
    .get(url, {
      headers: {
        'x-search-api-consumer': 'used-cars-page-hook',
        'x-search-experiment-data-driven-sort':
          shouldUseDataDrivenSortOrderAsDefault ? 'true' : 'false',
      },
    })
    .then((res) => res.data)

const getUrlForPageNumber = (page: number, asPath: string) => {
  const [urlPart, searchParamsPart] = asPath.split('?')

  const searchParams = new URLSearchParams(searchParamsPart)

  if (page === 1) {
    searchParams.delete('pageNumber')
  } else {
    searchParams.set('pageNumber', `${page}`)
  }

  if ([...searchParams.values()].length > 0) {
    // sort the query params to maintain a stable order
    searchParams.sort()
    return `${urlPart}?${searchParams}`
  }
  return urlPart
}

const getUrlWithSoftFilters = (asPath: string, softFilters: string[]) => {
  const [urlPart, searchParamsPart] = asPath.split('?')

  const searchParams = new URLSearchParams(searchParamsPart)

  softFilters.length > 0
    ? searchParams.set('q', softFilters.join(','))
    : searchParams.delete('q')

  // `qs` is an HMAC over the original `q`, so rewriting `q` invalidates it. We cannot re-sign
  // client side, and sending a signature that no longer matches is the same to the backend as
  // sending none, so drop it rather than carry a stale one.
  searchParams.delete('qs')

  // Removing a soft filter changes the result set, so we reset pagination to page 1
  searchParams.delete('pageNumber')

  if ([...searchParams.values()].length > 0) {
    // Sort the query params to maintain a stable order
    searchParams.sort()
    return `${urlPart}?${searchParams}`
  }
  return urlPart
}

const removeNaturalLanguageQueryFromWindowLocation = () => {
  if (typeof window === 'undefined') return

  const searchParams = new URLSearchParams(window.location.search)
  if (!searchParams.has('q') && !searchParams.has('qs')) return
  // `q` and `qs` are only meaningful as a pair, so they are always removed together
  searchParams.delete('q')
  searchParams.delete('qs')

  const query = searchParams.toString()
  const newUrl = `${window.location.pathname}${query ? `?${query}` : ''}`

  window.history.replaceState(window.history.state, '', newUrl)
}

const getDataFromResponse = (response: GetUsedCarsResponse | undefined) => {
  const searchResults = response
    ? {
        pageNumber: response.pageNumber,
        pageSize: response.pageSize,
        searchResultsCount: response.searchResultsCount,
        vehicleListings: response.vehicleListings,
        facets: response.facets,
      }
    : undefined

  return searchResults
}

/**
 * Extracts the value of the 'url' query parameter from a given API URL.
 *
 * @param apiUrl - The API URL from which to extract the 'url' query parameter.
 * @returns The value of the 'url' query parameter if it exists, or `null` if it does not exist
 *          or if the provided string is not a valid URL.
 */
const extractSearchUrl = (apiUrl: string): string | null => {
  try {
    const urlObj = new URL(apiUrl)
    return urlObj.searchParams.get('url') ?? null
  } catch {
    return null
  }
}

const getSoftFiltersFromNaturalLanguageQuery = (
  naturalLanguageQuery: string | null,
): string[] => {
  if (!naturalLanguageQuery) {
    return []
  }

  return naturalLanguageQuery
    .split(',')
    .map((softFilter) => softFilter.trim())
    .filter((softFilter) => softFilter.length > 0)
}

// stable empty reference so the optimistic "clearing" state doesn't trigger extra renders
const EMPTY_SOFT_FILTERS: string[] = []

// store the previous response so we can use it while waiting for new data,
// and so we can check the data has actually changed
let previousData: GetUsedCarsResponse | undefined

export function useUsedCarsData() {
  // NOTE: according the next.js useRouter docs, we shouldn't use `asPath` without checking `isReady`, and that can only be checked in a `useEffect`
  // Not doing so can cause issues when using SSR, with mismatches in the value between SSR and CSR
  // https://nextjs.org/docs/13/pages/api-reference/functions/use-router#router-object
  // But... looking at how it's used elsewhere in cinch-nx, no one is actually doing that (including us in the old useSearchFilters hook),
  // and no one has reported any issues, so we're going to use it as-is, without the `isReady` check.
  const { asPath, push } = useRouter()
  const { getExperienceIdForExperiment } = useSsrExperiments()
  const isAiSearchVariant =
    getExperienceIdForExperiment(readFromEnv(Env.AiSearchExperimentId)) === 1
  const requestUrl = getUsedCarsApiRequestUrlForUrlPath(
    asPath,
    isAiSearchVariant,
  )
  const softFiltersFromUrl = useMemo(
    () =>
      getSoftFiltersFromNaturalLanguageQuery(
        getNaturalLanguageQueryFromUrlPath(asPath, isAiSearchVariant),
      ),
    [asPath, isAiSearchVariant],
  )

  // Hard filters clear instantly (store state), while soft filters only clear once the URL
  // catches up on the next route change. This flag lets us hide the soft filters the moment
  // "clear all" is triggered, so the user never sees the leftover soft-filter count flash.
  const [isClearingSoftFilters, setIsClearingSoftFilters] = useState(false)

  useEffect(() => {
    if (isClearingSoftFilters && softFiltersFromUrl.length === 0) {
      setIsClearingSoftFilters(false)
    }
  }, [isClearingSoftFilters, softFiltersFromUrl])

  const searchParamMutators = useSearchStore(
    useShallow((state) => ({
      updateFilter: state.updateFilter,
      updateFilters: state.updateFilters,
      clearFilterValue: state.clearFilterValue,
      clearAllFilters: state.clearAllFilters,
      setPageNumber: state.setPageNumber,
      setSortingCriteria: state.setSortingCriteria,
      setUseMonthly: state.setUseMonthly,
    })),
  )

  const searchParams = useSearchStore(
    useShallow((state) => ({
      bodyType: state.bodyType,
      colour: state.colour,
      condition: state.condition,
      seats: state.seats,
      doors: state.doors,
      fromEngineSize: state.fromEngineSize,
      toEngineSize: state.toEngineSize,
      fuelType: state.fuelType,
      make: state.make,
      model: state.model,
      trim: state.trim,
      mileage: state.mileage,
      fromPrice: state.fromPrice,
      toPrice: state.toPrice,
      gearbox: state.gearbox,
      fromYear: state.fromYear,
      toYear: state.toYear,
      driveType: state.driveType,
      store: state.store,
      tags: state.tags,
      features: state.features,
      financeType: state.financeType,
      sortingCriteria: state.sortingCriteria,
      pageNumber: state.pageNumber,
      pageSize: state.pageSize,
      useMonthly: state.useMonthly,
      vehicleYear: state.vehicleYear,
      rangeInMiles: state.rangeInMiles,
      // Segment-only keys: never set by a filter control, populated from the URL path
      monthlyPrice: state.monthlyPrice,
      totalPrice: state.totalPrice,
    })),
  )

  const updateSearchParamsStateFromResponse = useSearchStore(
    useShallow((state) => state.updateSearchParamsStateFromResponse),
  )

  const shouldUseDataDrivenSortOrderAsDefault = true

  const {
    isValidating,
    isLoading,
    data,
    error: usedCarsApiError,
  } = useSWR(
    requestUrl,
    // SWR docs say not to do this (https://swr.vercel.app/docs/arguments#multiple-arguments), but it should be ok here, since the experiment state will
    // not change within a session, and this is only temporary until the experiment ends
    (url) => fetcher(url, shouldUseDataDrivenSortOrderAsDefault),
    {
      // the `keepPreviousData` property feels like it should do the same as this, but seems to have some subtle differences
      // as it causes values to not be updated correctly in some cases
      use: [keepPreviousDataWhileLoadingNewDataMiddleware],
    },
  )

  // The backend refuses a `q` whose `qs` signature does not verify, and reports it on the response.
  // SSR redirects those away before anything renders, so this is defence in depth: if the page is
  // ever reached without a fresh server pass, a rejected query still produces no chips. Presence of
  // the key is the signal — the key is absent for every other outcome, including the backend's own
  // errors, so a truthiness check here would strip chips during an outage on their side.
  const isNaturalLanguageQueryRejected =
    data !== undefined && 'naturalLanguageQueryRejected' in data

  const softFilters =
    isClearingSoftFilters || isNaturalLanguageQueryRejected
      ? EMPTY_SOFT_FILTERS
      : softFiltersFromUrl

  // when we get data back from the server, update the store to keep it in sync...
  // but only when the state has actually changed
  useEffect(() => {
    if (data && !isEqual(data, previousData)) {
      previousData = data
      updateSearchParamsStateFromResponse(
        data.parsedParamsFromUrl,
        data.pathSegmentsFromUrl,
      )
    }
  }, [data, updateSearchParamsStateFromResponse])

  const hardFilterCount = useMemo(
    () =>
      Object.entries(searchParams).reduce(
        (currentCount, [paramKey, paramValue]) => {
          // only increment the count if it's a filter param, things like pageSize/useMonthly/sortingCriteria, or financeType=any should not be included in this count
          if (
            !(searchFilterKeys as ReadonlyArray<string>).includes(paramKey) ||
            (paramKey === 'financeType' &&
              (paramValue as SearchFilters['financeType']).includes('any'))
          ) {
            return currentCount
          }
          if (Array.isArray(paramValue)) {
            return currentCount + paramValue.length
          }
          return currentCount + 1
        },
        0,
      ),
    [searchParams],
  )

  // soft filters count the same as hard filters towards the total
  const totalFilterCount = hardFilterCount + softFilters.length

  const pathSegmentFilters: Partial<SearchFilters> = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(data?.pathSegmentsFromUrl ?? {}).map(([key, value]) => [
          key,
          value.split(','),
        ]),
      ),
    [data],
  )

  const hasMatchingVehicleRecentlyBeenInStock =
    data?.hasMatchingVehicleRecentlyBeenInStock

  // get the relevant parts from either the current data or the previousData (if we're currently fetching new data)
  const searchResults = getDataFromResponse(data)

  // Extract the actual search URL
  const searchUrl = extractSearchUrl(requestUrl)

  // Removing a soft filter modifies the `q` param in the URL. Because the used-cars
  // request is keyed off the URL, the shallow route change triggers exactly one new
  // request to the Search Service. When the last term is removed, `q` is dropped entirely.
  const removeSoftFilter = (softFilter: string) => {
    const remainingSoftFilters = softFilters.filter(
      (currentSoftFilter) => currentSoftFilter !== softFilter,
    )

    push(getUrlWithSoftFilters(asPath, remainingSoftFilters), undefined, {
      shallow: true,
    })

    scrollToTarget()
  }

  // The store's `clearAllFilters` rebuilds the URL but preserves `q`/`qs` (read from
  // window.location). Strip them first so the store's single rebuild clears hard and soft filters in one navigation.
  const clearAllFilters = () => {
    removeNaturalLanguageQueryFromWindowLocation()
    setIsClearingSoftFilters(true)
    searchParamMutators.clearAllFilters()
  }

  return {
    // search results/API request props:
    isValidating,
    isLoading,
    usedCarsApiError,
    searchResults,
    hasMatchingVehicleRecentlyBeenInStock,
    // search filters/params props:
    searchParams,
    totalFilterCount,
    pathSegmentFilters,
    searchUrl,
    softFilters,
    // search filters/params setters:
    ...searchParamMutators,
    clearAllFilters,
    removeSoftFilter,
    // pagination:
    getUrlForPageNumber: (pageNumber: number) =>
      getUrlForPageNumber(pageNumber, asPath),
  }
}
